import { Experiment, ExperimentsValue } from '../typings/experimentsEditor';
import { RateControlValue } from '../typings/ratesCorrections';

export const OPEN_EXPERIMENTS_EDITOR = 'OPEN_EXPERIMENTS_EDITOR';
export type OPEN_EXPERIMENTS_EDITOR = typeof OPEN_EXPERIMENTS_EDITOR;

export interface OpenExperimentsEditorAction {
    type: OPEN_EXPERIMENTS_EDITOR;
    editorId: string;
    initialValue: ExperimentsValue;
    activeExperimentsLimit: number;
}

export function openExperimentsEditor(
    editorId: string,
    initialValue: ExperimentsValue,
    activeExperimentsLimit: number
): OpenExperimentsEditorAction {
    return {
        type: OPEN_EXPERIMENTS_EDITOR,
        editorId,
        initialValue,
        activeExperimentsLimit
    };
}

export const CLOSE_EXPERIMENTS_EDITOR = 'CLOSE_EXPERIMENTS_EDITOR';
export type CLOSE_EXPERIMENTS_EDITOR = typeof CLOSE_EXPERIMENTS_EDITOR;

export interface CloseExperimentsEditorAction {
    type: CLOSE_EXPERIMENTS_EDITOR;
    editorId: string;
}

export function closeExperimentsEditor(editorId: string): CloseExperimentsEditorAction {
    return {
        type: CLOSE_EXPERIMENTS_EDITOR,
        editorId
    };
}

export const TOGGLE_SEGMENT_TARGETING_EXPERIMENTS_EDITOR = 'TOGGLE_SEGMENT_TARGETING_EXPERIMENTS_EDITOR';
export type TOGGLE_SEGMENT_TARGETING_EXPERIMENTS_EDITOR = typeof TOGGLE_SEGMENT_TARGETING_EXPERIMENTS_EDITOR;

export interface ToggleSegmentTargetingExperimentsEditorAction {
    type: TOGGLE_SEGMENT_TARGETING_EXPERIMENTS_EDITOR;
    editorId: string;
}

export function toggleSegmentTargetingExperimentsEditor(
    editorId: string
): ToggleSegmentTargetingExperimentsEditorAction {
    return {
        type: TOGGLE_SEGMENT_TARGETING_EXPERIMENTS_EDITOR,
        editorId
    };
}

export const TOGGLE_CORRECTIONS_EXPERIMENTS_EDITOR = 'TOGGLE_CORRECTIONS_EXPERIMENTS_EDITOR';
export type TOGGLE_CORRECTIONS_EXPERIMENTS_EDITOR = typeof TOGGLE_CORRECTIONS_EXPERIMENTS_EDITOR;

export interface ToggleCorrectionsExperimentsEditorAction {
    type: TOGGLE_CORRECTIONS_EXPERIMENTS_EDITOR;
    editorId: string;
}

export function toggleCorrectionsExperimentsEditor(editorId: string): ToggleCorrectionsExperimentsEditorAction {
    return {
        type: TOGGLE_CORRECTIONS_EXPERIMENTS_EDITOR,
        editorId
    };
}

export const EDIT_SEARCH_VALUE_EXPERIMENTS_EDITOR = 'EDIT_SEARCH_VALUE_EXPERIMENTS_EDITOR';
export type EDIT_SEARCH_VALUE_EXPERIMENTS_EDITOR = typeof EDIT_SEARCH_VALUE_EXPERIMENTS_EDITOR;

export interface EditSearchValueExperimentsEditor {
    type: EDIT_SEARCH_VALUE_EXPERIMENTS_EDITOR;
    editorId: string;
    experiments: Experiment[];
    searchValue?: string;
}

export function editSearchValueExperimentsEditor(
    editorId: string,
    experiments: Experiment[],
    searchValue?: string
): EditSearchValueExperimentsEditor {
    return {
        type: EDIT_SEARCH_VALUE_EXPERIMENTS_EDITOR,
        editorId,
        experiments,
        searchValue
    };
}

export const SELECT_EXPERIMENT_TREE_NODE = 'SELECT_EXPERIMENT_TREE_NODE';
export type SELECT_EXPERIMENT_TREE_NODE = typeof SELECT_EXPERIMENT_TREE_NODE;

export interface SelectExperimentTreeNode {
    type: SELECT_EXPERIMENT_TREE_NODE;
    editorId: string;
    experiments: Experiment[];
    node: Experiment;
    isSegment: boolean;
    activeExperimentsLimit: number;
}

export function selectExperimentTreeNode(
    editorId: string,
    experiments: Experiment[],
    node: Experiment,
    isSegment: boolean,
    activeExperimentsLimit: number
): SelectExperimentTreeNode {
    return {
        type: SELECT_EXPERIMENT_TREE_NODE,
        editorId,
        experiments,
        node,
        isSegment,
        activeExperimentsLimit
    };
}

export const DESELECT_EXPERIMENT_TREE_NODE = 'DESELECT_EXPERIMENT_TREE_NODE';
export type DESELECT_EXPERIMENT_TREE_NODE = typeof DESELECT_EXPERIMENT_TREE_NODE;

export interface DeselectExperimentTreeNode {
    type: DESELECT_EXPERIMENT_TREE_NODE;
    editorId: string;
    experiments: Experiment[];
    node: Experiment;
    isSegment: boolean;
    savedArchivedExperimentsMap: Record<number, boolean>;
}

export function deselectExperimentTreeNode(
    editorId: string,
    experiments: Experiment[],
    node: Experiment,
    isSegment: boolean,
    savedArchivedExperimentsMap: Record<number, boolean>
): DeselectExperimentTreeNode {
    return {
        type: DESELECT_EXPERIMENT_TREE_NODE,
        editorId,
        experiments,
        node,
        isSegment,
        savedArchivedExperimentsMap
    };
}

export const TOGGLE_EXPERIMENT_TREE_NODE_EXPANSION = 'TOGGLE_EXPERIMENT_TREE_NODE_EXPANSION';
export type TOGGLE_EXPERIMENT_TREE_NODE_EXPANSION = typeof TOGGLE_EXPERIMENT_TREE_NODE_EXPANSION;

export interface ToggleExperimentTreeNodeExpansion {
    type: TOGGLE_EXPERIMENT_TREE_NODE_EXPANSION;
    editorId: string;
    node: Experiment;
}

export function toggleExperimentTreeNodeExpansion(
    editorId: string,
    node: Experiment
): ToggleExperimentTreeNodeExpansion {
    return {
        type: TOGGLE_EXPERIMENT_TREE_NODE_EXPANSION,
        editorId,
        node
    };
}

export const CHANGE_CORRECTIONS_VALUE_EXPERIMENTS_EDITOR = 'CHANGE_CORRECTIONS_VALUE_EXPERIMENTS_EDITOR';
export type CHANGE_CORRECTIONS_VALUE_EXPERIMENTS_EDITOR = typeof CHANGE_CORRECTIONS_VALUE_EXPERIMENTS_EDITOR;

export interface ChangeCorrectionsValueExperimentsEditor {
    type: CHANGE_CORRECTIONS_VALUE_EXPERIMENTS_EDITOR;
    editorId: string;
    segmentId: string;
    correctionValue: RateControlValue;
    minRate: number;
    maxRate: number;
}

export function changeCorrectionsValueExperimentsEditor(
    editorId: string,
    segmentId: string,
    correctionValue: RateControlValue,
    minRate: number,
    maxRate: number
): ChangeCorrectionsValueExperimentsEditor {
    return {
        type: CHANGE_CORRECTIONS_VALUE_EXPERIMENTS_EDITOR,
        editorId,
        segmentId,
        correctionValue,
        minRate,
        maxRate
    };
}

export const UPDATE_ARCHIVED_MAP_EXPERIMENTS_EDITOR = 'UPDATE_ARCHIVED_MAP_EXPERIMENTS_EDITOR';
export type UPDATE_ARCHIVED_MAP_EXPERIMENTS_EDITOR = typeof UPDATE_ARCHIVED_MAP_EXPERIMENTS_EDITOR;

export interface UpdateArchivedMapExperimentsEditor {
    type: UPDATE_ARCHIVED_MAP_EXPERIMENTS_EDITOR;
    editorId: string;
    archivedIds: string[];
}

export function updateArchivedMapExperimentsEditor(
    editorId: string,
    archivedIds: string[]
): UpdateArchivedMapExperimentsEditor {
    return {
        type: UPDATE_ARCHIVED_MAP_EXPERIMENTS_EDITOR,
        editorId,
        archivedIds
    };
}

export type ExperimentsEditorAction =
    | OpenExperimentsEditorAction
    | CloseExperimentsEditorAction
    | ToggleSegmentTargetingExperimentsEditorAction
    | ToggleCorrectionsExperimentsEditorAction
    | EditSearchValueExperimentsEditor
    | SelectExperimentTreeNode
    | DeselectExperimentTreeNode
    | ToggleExperimentTreeNodeExpansion
    | ChangeCorrectionsValueExperimentsEditor
    | UpdateArchivedMapExperimentsEditor;
